import React, { useCallback, useState } from 'react';
import { useAsyncValue, useNavigate, useRevalidator, useLocation } from 'react-router-dom';
import { useRevalidateOnSync } from '../../../../offline/useRevalidateOnSync';
import { useTranslation } from 'react-i18next';

// Styles
import { Container, ContainerButton } from '../components/MainStyledComponents';

// Types
import {
  tableIds,
  takeBase,
} from '../../../../components/display/tables/types';
import { firstPageTake } from '../../../../utils/cursor-take.helper';

// Components
import { ValidationsTable } from '../../../../components/display/tables/ValidationsTable';
import { MainButton } from '../../../../components/inputs/buttons/MainButton';
import { dataTypes } from '../../../../components/display/tables/ValidationsTable/types';

// Constants
import {
  formActionNaming,
  formActionValidationNaming,
} from '../../../../components/forms/ActionForms/constants';

// Utils
import { compressSessionStorage } from '../../../../utils/sessionStorage';

// GQL
import { client } from '../../../../gql/client';
import {
  GET_ACTION_BY_ID,
  FETCH_ACTIONS,
} from '../../../../gql/queries/actions/actions';
import { GET_ORDER_BY_ID } from '../../../../gql/queries/orders/orders';
import { selectActionOrders } from '../../../../gql/selectors/actions';

// Redux
import { actionTabsUnlockAll } from '../../../../state/actions/ui/actionTabs';
import { useDispatch } from 'react-redux';
import { getCursor } from '../../../../state/selectors/ui/tables';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import { store } from '../../../../state/configureStore';
import { externalCompaniesNaming } from '../../../../components/display/tables/formTables/ExternalCompaniesSelectionTable/constants';
import { usePollingRevalidation } from '../../../../hooks/usePollingRevalidation';
import { useCloseOrderConfirmation } from '../../../../hooks/useCloseOrderConfirmation';
import { withNavigationFeedback } from '../../../../utils/navigationFeedback';

export default function ActionsValidation(): JSX.Element {
  const [rowState, setRowState] = useState('');
  const loaderData = useAsyncValue() as dataTypes;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const revalidator = useRevalidator();
  useRevalidateOnSync();
  const { t } = useTranslation('pages');

  // T99: Modal de cerrar-OT (hook compartido). Tras cerrar nos quedamos en la
  // tabla; el revalidate del hook la refresca. Salta al volver aquí desde la
  // validación de una actuación.
  useCloseOrderConfirmation();

  // Abre la actuación `id`. Lo usan el botón (con la fila seleccionada) y el
  // doble toque de la tabla, para que los dos graben el mismo origen y vuelvan
  // al mismo sitio (spec 2026-10-06-return-to-origin § 4 E1).
  const openAction = useCallback((id: string) =>
    withNavigationFeedback(async () => {
    const {
      data: { action },
    } = await client.query({
      query: GET_ACTION_BY_ID,
      variables: { id },
    });

    const {
      data: { order },
    } = await client.query({
      query: GET_ORDER_BY_ID,
      variables: { id: action.orderIDs[0] },
    });
    Object.values({
      ...formActionNaming,
      ...formActionValidationNaming,
      ...externalCompaniesNaming,
    }).forEach((key) => {
      key !== formActionNaming.NEW_ACTION_DATA &&
        sessionStorage.removeItem(key);
    });

    sessionStorage.setItem(
      formActionNaming.NEW_VALIDATION_ACTION_DATA,
      compressSessionStorage(
        JSON.stringify({
          id,
          orderId: order.id,
          roid: order.roid,
          isActionEdited: 'false',
          origin: 'validation-table',
          parentOrderId: order.id,        // Parent order ID for redirect
          parentRoid: order.roid,         // Parent order ROID for campaign detection
          entityRoid: action.roid,        // Action ROID for breadcrumb
          returnTo: '/work-orders/actions-validation',
        })
      )
    );

    // FLUJO VALIDACIÓN: Desbloquear TODAS las tabs porque es una acción existente
    dispatch(actionTabsUnlockAll());
    navigate(`summary/${id}`);
  }), [dispatch, navigate]);

  const handleClick = useCallback(() => openAction(rowState), [openAction, rowState]);

  const onReFetchData = useCallback(async () => {
    // Always read from store.getState() — avoids stale closure
    const currentCursor = getCursor(tableIds.VALIDATION_ACTIONS)(store.getState());
    // Tarea #11: respetar el take guardado en cursor (page-size selector).
    const effectiveTake = firstPageTake(currentCursor.take, takeBase);
    const { data } = await client.query({
      query: FETCH_ACTIONS,
      variables: {
        input: {
          take: effectiveTake,
          cursor: '',
          sortField: currentCursor?.sortField,
          orderBy: currentCursor?.orderBy ?? 'desc',
          concessionId: currentCursor.concessionId,
          toDate: currentCursor.toDate,
          fromDate: currentCursor.fromDate,
          indexType: currentCursor.indexType,
          indexExpedition: currentCursor.indexExpedition,
          classification: currentCursor.classification,
          predefinedOrderId: currentCursor.predefinedOrderId,
          capitolId: currentCursor.capitolId,
          subCapitolId: currentCursor.subCapitolId,
          states: currentCursor.states,
          userIds: currentCursor.userIds,
          operatorIds: currentCursor.operatorIds,
          teamIds: currentCursor.teamIds,
          externalCompanyId: currentCursor.externalCompanyId,
          containerTypes: currentCursor.containerTypes,
          track: currentCursor.track,
          activityId: currentCursor.activityId,
          // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
          onlyFlagged: currentCursor?.onlyFlagged,
          pkInitKm: currentCursor?.pkInitKm,
          pkInitMeter: currentCursor?.pkInitMeter,
          pkEndKm: currentCursor?.pkEndKm,
          pkEndMeter: currentCursor?.pkEndMeter,
        },
      },
      fetchPolicy: 'network-only',
    });

    const setCursorData = {
      take: effectiveTake,
      cursor: '',
      concessionId: currentCursor?.concessionId,
      toDate: currentCursor?.toDate,
      fromDate: currentCursor?.fromDate,
      indexType: currentCursor?.indexType,
      indexExpedition: currentCursor?.indexExpedition,
      classification: currentCursor?.classification,
      predefinedOrderId: currentCursor?.predefinedOrderId,
      capitolId: currentCursor?.capitolId,
      subCapitolId: currentCursor?.subCapitolId,
      states: currentCursor?.states,
      userIds: currentCursor?.userIds,
      operatorIds: currentCursor?.operatorIds,
      teamIds: currentCursor?.teamIds,
      externalCompanyId: currentCursor?.externalCompanyId,
      containerTypes: currentCursor?.containerTypes,
      track: currentCursor?.track,
      activityId: currentCursor?.activityId,
      onlyFlagged: currentCursor?.onlyFlagged,
      pkInitKm: currentCursor?.pkInitKm,
      pkInitMeter: currentCursor?.pkInitMeter,
      pkEndKm: currentCursor?.pkEndKm,
      pkEndMeter: currentCursor?.pkEndMeter,
    };

    dispatch(setCursor(tableIds.VALIDATION_ACTIONS, setCursorData));
    await selectActionOrders(data.actions.actions, true);
    revalidator.revalidate();
  }, []);

  const onRefetchPagination = useCallback(async () => {
    revalidator.revalidate();
  }, []);

  // Polling: refresca datos de pág 1 en cache (network-only) sin cambiar posición del usuario
  const onPollingRefresh = useCallback(async () => {
    const currentCursor = getCursor(tableIds.VALIDATION_ACTIONS)(store.getState());
    const effectiveTake = firstPageTake(currentCursor.take, takeBase);
    await client.query({
      query: FETCH_ACTIONS,
      variables: {
        input: {
          take: effectiveTake,
          cursor: '',
          sortField: currentCursor?.sortField,
          orderBy: currentCursor?.orderBy ?? 'desc',
          concessionId: currentCursor.concessionId,
          toDate: currentCursor.toDate,
          fromDate: currentCursor.fromDate,
          indexType: currentCursor.indexType,
          indexExpedition: currentCursor.indexExpedition,
          classification: currentCursor.classification,
          predefinedOrderId: currentCursor.predefinedOrderId,
          capitolId: currentCursor.capitolId,
          subCapitolId: currentCursor.subCapitolId,
          states: currentCursor.states,
          userIds: currentCursor.userIds,
          operatorIds: currentCursor.operatorIds,
          teamIds: currentCursor.teamIds,
          externalCompanyId: currentCursor.externalCompanyId,
          containerTypes: currentCursor.containerTypes,
          track: currentCursor.track,
          activityId: currentCursor.activityId,
          // Sin esto la lista filtrada dejaría de refrescarse en silencio.
          onlyFlagged: currentCursor?.onlyFlagged,
          pkInitKm: currentCursor?.pkInitKm,
          pkInitMeter: currentCursor?.pkInitMeter,
          pkEndKm: currentCursor?.pkEndKm,
          pkEndMeter: currentCursor?.pkEndMeter,
        },
      },
      fetchPolicy: 'network-only',
    });
    revalidator.revalidate();
  }, []);
  usePollingRevalidation(onPollingRefresh);
  return (
    <Container>
      <ValidationsTable
        titleText={t('validation.filterActions')}
        setRowState={setRowState}
        data={loaderData}
        tableId={tableIds.VALIDATION_ACTIONS}
        onReFetchData={onReFetchData}
        onReFetchPagination={onRefetchPagination}
        onRowOpen={openAction}
        // Banderitas (spec 2026-10-22 § 8): columna y filtro «Solo marcadas».
        showFlagColumn
        showOnlyFlaggedFilter
      />
      <ContainerButton>
        <MainButton
          text={t('validation.viewAction')}
          disabled={rowState.length === 0}
          onClick={handleClick}
        />
      </ContainerButton>
    </Container>
  );
}
